import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { gql } from '@graphql/__generated__';
import {
  Activity_Types_Enum_Enum,
  type Lead_Agent_Source_Enum_Enum,
  type Leads_Insert_Input,
  type Leads_Relationship_Enum,
  type Leads_Sale_Horizon_Enum,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getDefaultSubject } from '@modules/_shared/utils/activity';
import { useLocale } from '@shared/hooks/locale';

const INSERT_LEAD = gql(/* GraphQL */ `
  mutation InsertLead(
    $input: leads_insert_input!
    $lead_id: uuid!
    $broker_id: uuid!
    $include_call_activity: Boolean!
    $claimed_at: timestamptz!
    $stage_id: uuid!
    $origination_activity: activities_insert_input!
    $assignment_activity: activities_insert_input!
    $call_activity: activities_insert_input!
  ) {
    insert_leads_one(object: $input) {
      id
      property_id
    }

    # Create origination activity
    origination: insert_activities_one(object: $origination_activity) {
      id
    }

    # Create assignment activity
    assignment: insert_activities_one(object: $assignment_activity) {
      id
    }

    # Create call activity with target user
    call: insert_activities_one(object: $call_activity)
      @include(if: $include_call_activity) {
      id
      activities_target_users {
        target_user_id
      }
    }

    insert_lead_agents_one(
      object: {
        lead_id: $lead_id
        user_id: $broker_id
        source: crm
        claimed_at: $claimed_at
        stage_id: $stage_id
      }
    ) {
      id
    }
  }
`);

const GET_LEAD_METADATA = gql(/* GraphQL */ `
  query GetLeadMetadata($sourceName: String!) {
    pipelines(where: { name: { _eq: "brokerage" } }, limit: 1) {
      id
      stages(where: { name: { _eq: "claimed" } }, limit: 1) {
        id
        name
      }
    }
    dictionaries(
      where: { type: { _eq: lead_source_types }, name: { _eq: $sourceName } }
      limit: 1
    ) {
      id
    }
  }
`);

export type CreateLeadInput = {
  stage_id?: string | null;
  contact?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  property: {
    lat?: number | null;
    lng?: number | null;
    route?: string | null;
    street_number?: string | null;
    postcode?: string | null;
    state?: string | null;
    locality?: string | null;
    country_code?: string | null;
    property_type_id?: string | null;
    living_surface?: number | null;
    land_surface?: number | null;
    number_of_rooms?: number | null;
    number_of_bedrooms?: number | null;
    built_surface?: number | null;
    construction_year?: number | null;
    renovation_year?: number | null;
  };
  relationship?: Leads_Relationship_Enum | null;
  sale_horizon?: Leads_Sale_Horizon_Enum | null;
  source?: Lead_Agent_Source_Enum_Enum | null;
  broker_id?: string;
};

type CreateLeadHookResult = [
  ErrorLike | null,
  boolean,
  (
    input: CreateLeadInput,
    includeCallActivity?: boolean,
  ) => Promise<{ leadId: string; propertyId: string } | null>,
  () => void, // resetError function
];

export const useCreateLead = (): CreateLeadHookResult => {
  const { me } = useAppData();
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [insertLead, { loading: insertLeadLoading }] = useMutation(
    INSERT_LEAD,
    {
      refetchQueries: ['GetLeads', 'GetLeadsCount'],
      onError: setErrorLike,
    },
  );

  const [getLeadMetadata, { loading: metadataLoading }] = useLazyQuery(
    GET_LEAD_METADATA,
    { notifyOnNetworkStatusChange: true },
  );

  const getSourceId = async (sourceName: string): Promise<string> => {
    const { data } = await getLeadMetadata({
      variables: { sourceName },
    });

    if (!data?.dictionaries[0]?.id) {
      throw new Error(`No match found for lead source name "${sourceName}"`);
    }

    return data.dictionaries[0].id;
  };

  const getStageId = async (): Promise<string> => {
    const { data } = await getLeadMetadata({
      variables: { sourceName: 'crm' }, // Default source for stage query
    });

    if (!data?.pipelines[0]?.id) {
      throw new Error('Brokerage pipeline not found');
    }

    const claimedStage = data.pipelines[0].stages[0];

    if (!claimedStage?.id) {
      throw new Error('No claimed stage found in brokerage pipeline');
    }

    return claimedStage.id;
  };

  const createNewLead = async (
    input: CreateLeadInput,
    includeCallActivity = true,
  ) => {
    // We want to force the stage_id (use for leads created from kanban)
    const source = input.source ?? 'crm';
    const sourceId = await getSourceId(source);
    const stage_id = input.stage_id ?? (await getStageId());

    // We delay the created_at otherwise the activity order is wrong in the feed
    const now = new Date();
    const plus5s = new Date(now.getTime() + 5000);
    const plus10s = new Date(now.getTime() + 10000);

    const generatedId = uuidv4();

    const leadInput: Leads_Insert_Input = {
      id: generatedId,
      source_id: sourceId,
      // Lead is always completed for CRM
      completed: true,

      // Used to leverage the anonymized contact field
      first_name: input.contact?.first_name,
      last_name: input.contact?.last_name,
      contact_id: input.contact?.id,
      created_by: me?.id,

      relationship: input.relationship ?? undefined,
      sale_horizon: input.sale_horizon ?? undefined,

      property: {
        data: input.property,
      },
    };

    const contactName =
      `${input.contact?.first_name} ${input.contact?.last_name}`.trim();
    const callSubject = getDefaultSubject({
      t,
      activityType: 'call',
      assignedTo: {
        firstName: me?.first_name ?? null,
        lastName: me?.last_name ?? null,
        teamName: me?.default_team?.name ?? null,
      },
      participants: [{ name: contactName }],
    });

    const { data: leadData, error: leadError } = await insertLead({
      variables: {
        input: leadInput,
        lead_id: generatedId,
        broker_id: input.broker_id ?? me?.id ?? '',
        include_call_activity: includeCallActivity && !!input.contact?.id,
        claimed_at: plus10s.toISOString(),
        stage_id,
        origination_activity: {
          lead_id: generatedId,
          activity_type: Activity_Types_Enum_Enum.Origination,
          done: true,
          success: true,
          done_at: now.toISOString(),
          created_at: now.toISOString(),
          ...(me?.is_admin ? { created_by: me?.id } : {}),
        },
        assignment_activity: {
          lead_id: generatedId,
          activity_type: Activity_Types_Enum_Enum.Assignment,
          assigned_to: input.broker_id ?? me?.id ?? '',
          done: true,
          success: true,
          done_at: plus5s.toISOString(),
          created_at: plus5s.toISOString(),
          ...(me?.is_admin ? { created_by: me?.id } : {}),
        },
        call_activity: {
          lead_id: generatedId,
          activity_type: Activity_Types_Enum_Enum.Call,
          assigned_to: input.broker_id ?? me?.id ?? '',
          created_at: plus10s.toISOString(),
          start_date: format(now, 'yyyy-MM-dd'),
          subject: callSubject,
          activities_target_users: {
            data: input.contact?.id
              ? [{ target_user_id: input.contact.id }]
              : [],
          },
          ...(me?.is_admin ? { created_by: me?.id } : {}),
        },
      },
    });

    if (leadError || !leadData?.insert_leads_one) {
      return null;
    }

    return {
      leadId: leadData.insert_leads_one.id,
      // We just sent the property data in the lead input, so we are sure that the property id is in the lead data
      propertyId: leadData.insert_leads_one.property_id as string,
    };
  };

  return [
    apolloError,
    insertLeadLoading || metadataLoading,
    createNewLead,
    () => {
      setErrorLike(null);
    },
  ];
};
